import { Box, Typography, Card, alpha } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import InsightsIcon from '@mui/icons-material/Insights';
import Grid from '@mui/material/Grid';
import { colors } from '../../theme';

interface FeatureItemProps {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const FeatureItem = ({ icon, title, description }: FeatureItemProps) => (
    <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: alpha(colors.secondary, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.secondary,
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <Box>
            <Typography
                sx={{
                    fontWeight: 600,
                    color: colors.slate[900],
                    fontSize: '0.875rem',
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: colors.slate[500], mt: 0.25 }}
            >
                {description}
            </Typography>
        </Box>
    </Box>
);

export const AnalyticsDashboard = () => (
    <Box>
        {/* Page Header */}
        <Box mb={4}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    letterSpacing: '-0.5px',
                }}
            >
                Analytics
            </Typography>
            <Typography
                variant="body1"
                sx={{ color: colors.slate[500], mt: 0.5 }}
            >
                Insights and trends for your XTConnect system
            </Typography>
        </Box>

        {/* Coming Soon Card */}
        <Card sx={{ overflow: 'hidden' }}>
            {/* Gradient Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${colors.secondary}, #8b5cf6)`,
                    p: 4,
                    textAlign: 'center',
                }}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        background: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <BarChartIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: 'white', mb: 1 }}
                >
                    Coming Soon
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        maxWidth: 400,
                        mx: 'auto',
                    }}
                >
                    Advanced analytics and reporting to help you understand
                    system usage and trends.
                </Typography>
            </Box>

            {/* Features Preview */}
            <Box sx={{ p: 4 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: colors.slate[900],
                        mb: 3,
                    }}
                >
                    Planned Features
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
                            title="Usage Trends"
                            description="Track device activity and user engagement over time"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<TimelineIcon sx={{ fontSize: 20 }} />}
                            title="Session Analytics"
                            description="Monitor tech support session patterns and durations"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<InsightsIcon sx={{ fontSize: 20 }} />}
                            title="Device Health"
                            description="Visualize device status, connectivity, and data collection"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<BarChartIcon sx={{ fontSize: 20 }} />}
                            title="Custom Reports"
                            description="Generate exportable reports for stakeholders"
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    </Box>
);